import { Navigate, useSearchParams } from 'react-router-dom';
import { useMe } from '../../api/hooks';
import { useIsStaff, useLearningOverview } from '../../api/learning';
import { ErrorView, Loading, Screen } from '../../ui/Screen';
import { DecisionsTab } from './DecisionsTab';
import { ErrorsTab } from './ErrorsTab';
import { ReviewTab } from './ReviewTab';
import { ExperimentsTab } from './ExperimentsTab';
import { PlaybookTab } from './PlaybookTab';
import { ReportTab } from './ReportTab';

type Tab = 'report' | 'decisions' | 'experiments' | 'playbook' | 'errors' | 'review';
const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'report', label: 'Report' },
  { id: 'decisions', label: 'Decisions' },
  { id: 'experiments', label: 'Experiments' },
  { id: 'playbook', label: 'Playbook' },
  { id: 'errors', label: 'Errors' },
  { id: 'review', label: 'Review' },
];

/** Staff only: everyone else goes back to the reel list. */
export function LearningScreen() {
  const me = useMe();
  const staff = useIsStaff();
  if (me.isError) return <ErrorView title="Learning" back="/" error={me.error} />;
  if (staff === undefined) return <Loading title="Learning" />;
  if (!staff) return <Navigate to="/" replace />;
  return <LearningArea />;
}

/** The improvement loop: report, decisions, experiments and the playbook. */
function LearningArea() {
  const [params, setParams] = useSearchParams();
  const tab = (TABS.find((t) => t.id === params.get('tab'))?.id ?? 'report') as Tab;
  const overview = useLearningOverview();
  const open = overview.data?.openDecisions ?? 0;
  return (
    <Screen title="Learning" back="/" width="wide">
      <div className="seg seg-tabs" role="tablist" aria-label="Learning views">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            id={`ltab-${t.id}`}
            aria-selected={tab === t.id}
            aria-controls={`lpanel-${t.id}`}
            onClick={() => setParams(t.id === 'report' ? {} : { tab: t.id }, { replace: true })}
          >
            {t.label}
            {t.id === 'decisions' && open > 0 && (
              <span className="badge" aria-label={`${open} open`}>
                {open}
              </span>
            )}
          </button>
        ))}
      </div>
      <div
        role="tabpanel"
        id={`lpanel-${tab}`}
        aria-labelledby={`ltab-${tab}`}
        style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        {tab === 'report' && <ReportTab />}
        {tab === 'decisions' && <DecisionsTab />}
        {tab === 'experiments' && <ExperimentsTab />}
        {tab === 'playbook' && <PlaybookTab />}
        {tab === 'errors' && <ErrorsTab />}
        {tab === 'review' && <ReviewTab />}
      </div>
    </Screen>
  );
}
